import React, { forwardRef } from 'react';
import DataGrid, {
  Column,
  FilterRow,
  HeaderFilter,
  SearchPanel,
  Export,
  Toolbar,
  Item,
  Paging,
  Pager,
  Editing,
  LoadPanel,
  Selection,
  Scrolling,
} from 'devextreme-react/data-grid';
import DropDownButton from 'devextreme-react/drop-down-button';
import Button from 'devextreme-react/button';
import '../../style/DataGridList.scss';

const DataGridList = forwardRef(({

  data = [],
  keyExpr = 'id',
  gridRef = null,

  gridTitle = 'New',
  isLoading = false,
  showBorders = true,
  columnAutoWidth = true,
  allowColumnResizing = true,
  wordWrapEnabled = true,

  toolbarVisible = true,
  onRefresh = null,
  onAdd = null,
  addButtonText,
  filterButtonVisible = false,
  filterData = [],
  filterButtonText = "Filter",
  onFilterItemClick = null,
  showGridTitle = false,
  autoRefreshControl = null,
  updateAllControl = null,
  refreshControl = null,

  columns = [],
  columnButtons = [],

  onDoubleClick = null,
  onFocusedRowChanged = null,
  focusedRowKey = null,
  focusedRowEnabled = true,

  onRowPrepared = null,
  onRowUpdating = null,
  onRowRemoving = null,

  showFilterRow = false,
  showHeaderFilter = true,
  showSearchPanel = true,
  highlightCaseSensitive = true,

  exportEnabled = false,
  exportFileName = 'Export',
  allowExportSelectedData = true,

  selectionMode = 'multiple',
  showCheckBoxesMode = 'none',
  onSelectionChanged = null,

  pageSize = 10,
  allowedPageSizes = [5, 10, 20, 30, 50, 100],
  showPager = true,
  showInfo = true,
  showNavigationButtons = true,

  allowAdding = false,
  allowDeleting = false,
  allowUpdating = false,

  showLoadPanel = true,
  height = '100%',
}, ref) => {
  const actualGridRef = ref || gridRef;

  return (
    <div className="crm-grid-container" style={height === 'auto' ? { height: 'auto', flex: 'none' } : {}}>
      <DataGrid
        ref={actualGridRef}
        dataSource={data}
        keyExpr={keyExpr}
        repaintChangesOnly={true}
        showBorders={showBorders}
        columnAutoWidth={columnAutoWidth}
        allowColumnResizing={allowColumnResizing}
        wordWrapEnabled={wordWrapEnabled}
        focusedRowEnabled={focusedRowEnabled}
        focusedRowKey={focusedRowKey}
        onSelectionChanged={onSelectionChanged}
        onFocusedRowChanged={onFocusedRowChanged}
        onRowDblClick={onDoubleClick}
        onRowUpdating={onRowUpdating}
        onRowRemoving={onRowRemoving}
        loading={isLoading && (!data || data.length === 0)}
        onRowPrepared={onRowPrepared}
        height={height}
      >
        <Selection mode={selectionMode} showCheckBoxesMode={showCheckBoxesMode} />
        <Scrolling mode="standard" showScrollbar="onHover" />
        <FilterRow visible={showFilterRow} />
        <HeaderFilter visible={showHeaderFilter} />
        <SearchPanel
          visible={showSearchPanel}
          highlightCaseSensitive={highlightCaseSensitive}
          applySearch={true}
        />

        {exportEnabled && (
          <Export
            enabled={true}
            fileName={exportFileName}
            allowExportSelectedData={allowExportSelectedData}
          />
        )}

        {showLoadPanel && isLoading && (!data || data.length === 0) && <LoadPanel showPane={true} />}

        <Paging defaultPageSize={pageSize} />
        {showPager && (
          <Pager
            showPageSizeSelector={true}
            allowedPageSizes={allowedPageSizes}
            displayMode="compact"
            visible={true}
            showInfo={showInfo}
            showNavigationButtons={showNavigationButtons}
          />
        )}

        {toolbarVisible && (
          <Toolbar>
            {showGridTitle && (
              <Item location="before" locateInMenu="auto">
                <h2 className="portal-panel-title" style={{ margin: 0, paddingRight: "16px" }}>{gridTitle}</h2>
              </Item>
            )}
            {filterButtonVisible && filterData && filterData.length > 0 && (
              <Item location="before" locateInMenu="auto">
                <DropDownButton 
                  stylingMode="text" 
                  text={filterButtonText} 
                  dataSource={filterData} 
                  onItemClick={onFilterItemClick}
                  displayExpr="text"
                  keyExpr="value"
                />
              </Item>
            )}
            {onAdd && (
              <Item location="after">
                <Button
                  stylingMode="contained"
                  icon="add"
                  text={addButtonText || `Add ${gridTitle}`}
                  onClick={onAdd}
                />
              </Item>
            )}
            {autoRefreshControl && (
              <Item location="after" locateInMenu="auto">
                {autoRefreshControl}
              </Item>
            )}
            {updateAllControl && (
              <Item location="after" locateInMenu="auto">
                {updateAllControl}
              </Item>
            )}
            {refreshControl && (
              <Item location="after" locateInMenu="auto">
                {refreshControl}
              </Item>
            )}
            {onRefresh && !refreshControl && (
              <Item location="after">
                <Button
                  stylingMode="text"
                  icon="refresh"
                  type="default"
                  onClick={onRefresh}
                />
              </Item>
            )}
            <Item name="exportButton" />
            <Item name="searchPanel" />
          </Toolbar>
        )}

        {columns.map((col, index) => {
          const {
            name,
            dataField,
            caption,
            dataType,
            visible,
            width,
            cellRender,
            calculateCellValue,
            calculateDisplayValue,
            allowEditing,
            sortOrder,
            defaultSortOrder,
            ...rest
          } = col;
          return (
            <Column
              key={name || dataField || caption || index}
              name={name}
              dataField={dataField}
              caption={caption}
              dataType={dataType}
              visible={visible ?? true}
              width={width}
              sortOrder={sortOrder}
              defaultSortOrder={defaultSortOrder}
              cellRender={cellRender}
              calculateCellValue={calculateCellValue}
              calculateDisplayValue={calculateDisplayValue}
              allowEditing={allowEditing}
              {...rest}
            />
          );
        })}

        {columnButtons.length > 0 && (
          <Column type="buttons" width={110} fixed={true} fixedPosition="right" buttons={columnButtons} />
        )}

        <Editing
          mode="row"
          allowAdding={allowAdding}
          allowDeleting={allowDeleting}
          allowUpdating={allowUpdating}
        />
      </DataGrid>
    </div>
  );
});

export default DataGridList;